"use client";

import React from "react";
import { Upload, Calculator, SquareCheck as CheckSquare, Package } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  num: "1",
  title: "Upload your file or panel layout",
  desc: "DXF, DWG, AI, PDF, or SVG, or a marked-up legend list.",
  icon: Upload
}, {
  num: "2",
  title: "Fast quote",
  desc: "Material, color, mounting, quantity pricing, and a real lead time, with rush flagged.",
  icon: Calculator
}, {
  num: "3",
  title: "Approve the proof",
  desc: "Confirm text, layout, and dimensions in writing before engraving.",
  icon: CheckSquare
}, {
  num: "4",
  title: "Production & ship",
  desc: "Engraved on the specified material and shipped install-ready.",
  icon: Package
}];
export default function TurnaroundHowItWorks() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const footerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="turnaround-how-it-works" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={headerReveal.ref} className={`max-w-3xl mb-12 md:mb-16 transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            How Fast Can Evermark Turn Around a Contractor Order?
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Evermark Industrial Solutions quotes a real lead time on every
            contractor order, with rush production when the schedule demands it.
            Standard and rush turnaround:{" "}
            <span className="font-semibold text-foreground">
              {"{{verify: standard lead time}}"}
            </span>{" "}
            standard,{" "}
            <span className="font-semibold text-foreground">
              {"{{verify: rush lead time}}"}
            </span>{" "}
            rush — confirmed on the quote.
          </p>
        </div>

        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-16 transition-all duration-700 delay-200 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <Card key={i} data-index={i} className="relative overflow-hidden border-border/50 shadow-md hover:shadow-lg transition-shadow duration-300 bg-card">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-6xl font-serif font-bold text-primary/20 select-none">
                      {step.num}
                    </span>
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-xl font-default text-card-foreground leading-tight">
                    {step.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-muted-foreground">
                    {step.desc}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        <div ref={footerReveal.ref} className={`flex flex-col items-start gap-8 transition-all duration-700 delay-400 ease-out ${footerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="p-6 bg-muted/50 rounded-xl border border-border/50 max-w-4xl">
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed"><strong className="text-foreground font-semibold" data-ast-id="f:/src/pages/ElectricalContractors/sections/TurnaroundHowItWorks.tsx|sig:rv4ov3" data-ast-start="4682" data-ast-end="4746">Note:</strong> Actual lead time depends on material, quantity, and finishing — Evermark commits to a date on the quote rather than a generic promise.</p>
          </div>

          <Button asChild size="lg" className="text-base px-8 h-12">
            <Link to="/ContactPage">Request a Quote</Link>
          </Button>
        </div>
      </div>
    </section>;
}